import { httpClient } from 'app/api/http/http-client';
import {
  ClosePopupRequestType,
  HomePageResponseType,
  PopupResponseType,
} from 'app/utils/services/types/homePage.type';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { groupNotificationsByDate } from '../groupNotificationsByDate';

export const homePageService = {
  getHomePage: async (): Promise<HomePageResponseType> => {
    return await httpClient.post('/api-web/v1/main/home-page');
  },

  changeLangHomePage: async () => {
    return await httpClient.post<any>(`/api-web/v1/main/get_header_menu`);
  },

  getHeaderAndMenu: async () => {
    const is_guest = JSON.parse(sessionStorage.getItem('is_guest'));

    return await httpClient.post<any>(
      '/api-web/v1/main/get_header_menu',
      is_guest ? { is_guest: true } : null
    );
  },
  getPopup: async (): Promise<PopupResponseType> => {
    return await httpClient.post('/api-web/v1/main/ib_popup');
  },
  // Tells the backend the popup was seen, so it is not served again
  closePopup: async (data: ClosePopupRequestType): Promise<PopupResponseType> => {
    return await httpClient.post('/api-web/v1/main/ib_popup', data);
  },
  getNotifications: async () => {
    const result = await httpClient.post<any>('/api-web/v1/main/ib_notification');

    return groupNotificationsByDate(result?.new_notifications_list || []);
  },
  changeNotificationStatus: async (id: string): Promise<any> => {
    const result = await httpClient.post<any>('/api-web/v1/main/ib_notification', {
      id,
    });

    return result;
  },
};

export const useGetHomePageService = () => {
  const {
    isLoading: isLoadingGetHomePage,
    data: dataGetHomePage,
    refetch: refetchGetHomePage,
  } = useQuery(['getHomePage'], homePageService.getHomePage, { cacheTime: 0 });

  return { isLoadingGetHomePage, dataGetHomePage, refetchGetHomePage };
};

export const useGetPopupService = () => {
  const {
    isLoading: isLoadingGetPopup,
    data: dataGetPopup,
    refetch: refetchGetPopup,
  } = useQuery(['getPopup'], homePageService.getPopup, { cacheTime: 0 });

  return { isLoadingGetPopup, dataGetPopup, refetchGetPopup };
};

export const useClosePopupService = () => {
  const {
    isLoading: isLoadingClosePopup,
    mutate: closePopupMutation,
    mutateAsync: closePopupAsync,
  } = useMutation(['closePopup'], homePageService.closePopup);

  return { isLoadingClosePopup, closePopupMutation, closePopupAsync };
};

export const useChangeLangHomePageService = (path: string) => {
  const {
    isLoading: isLoadingChangeLangHomePage,
    isSuccess,
    data: dataChangeLangHomePage,
    mutate: homeChangeLangPageMutation,
  } = useMutation(['homePageLang'], () => homePageService.changeLangHomePage(), {
    onSuccess: async () => {
      try {
        const result = await httpClient.post<HomePageResponseType>(
          `/api-web/v1/main/${path}`,
          null
        );

        return result;
      } catch (err: any) {
        if (err?.response.status === 400) {
          return err?.response.data;
        }
      }
    },
  });

  return {
    isLoadingChangeLangHomePage,
    isSuccess,
    dataChangeLangHomePage,
    homeChangeLangPageMutation,
  };
};

export const useGetHeaderAndMenu = () => {
  const {
    isLoading: isLoadingHeaderAndMenu,
    data: dataHeaderAndMenu,
    refetch: refetchGetHeaderAndMenu,
  } = useQuery(['getHeaderAndMenu'], () => homePageService.getHeaderAndMenu());

  return { isLoadingHeaderAndMenu, dataHeaderAndMenu, refetchGetHeaderAndMenu };
};

// Triggers a fresh header/menu fetch and syncs the shared 'getHeaderAndMenu'
// query cache, without mounting its own query observer. Use this when only
// the refetch action is needed (e.g. after a language change) so an extra
// GET isn't fired alongside the query already held by the layout.
export const useRefetchHeaderAndMenu = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: refetchGetHeaderAndMenu } = useMutation(homePageService.getHeaderAndMenu, {
    onSuccess: data => {
      queryClient.setQueryData(['getHeaderAndMenu'], data);
    },
  });

  return { refetchGetHeaderAndMenu };
};
